class SubscriptionModal extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.subscriptions = [];
    this.product = null;
    this.productVariant = null;
    this.productId = null;
    this.quantity = 1;
    this.productSubscriptionInterval = 2;
    this.quantityChangedInModal = false;
    this.price = null;
    this.nextBillingDate = null;
    this.handleCloseClick = () => this.close();
  }

  connectedCallback() {
    this.render();
    this.initQuantityControls();
    this.addEventListener('click', (e) => {
      const target = e.target.closest('.subscription-modal__confirm');
      if (target) {
        this.handleAddProductClick();
      }
    });
  }

  getFirstNonBlikSellingPlan() {
    if (!this.product || !Array.isArray(this.product.selling_plan_groups)) return null;
    return this.product.selling_plan_groups.find((plan) => !/blik/i.test(plan.name));
  }

  setProductVariantPrice(variantId) {
    if (!this.product || !Array.isArray(this.product.variants)) {
      console.warn('Brak danych produktu lub wariantów.');
      return;
    }

    const variant = this.product.variants.find((v) => String(v.id) === String(variantId));
    const group = this.getFirstNonBlikSellingPlan();
    const id = group?.id;

    if (variant && id) {
      const sellingPlan = variant.selling_plan_allocations.find((v) => v.selling_plan_group_id === id);
      this.price = sellingPlan?.price ? sellingPlan.price / 100 : null;
    } else {
      console.warn(`Nie znaleziono wariantu o id ${variantId}`);
    }
  }

  initQuantityControls() {
    if (!this.modal) return;
    const minusBtn = this.modal.querySelector('button[name="minus"]');
    const plusBtn = this.modal.querySelector('button[name="plus"]');
    const quantityInput = this.modal.querySelector('.quantity_input');

    minusBtn?.addEventListener('click', () => {
      let val = parseInt(quantityInput.value, 10) || 1;
      if (val > 1) {
        val--;
        quantityInput.value = val;
        this.quantity = val;
        this.quantityChangedInModal = true;
        this.updateQuantityAndPrice();
      }
    });

    plusBtn?.addEventListener('click', () => {
      let val = parseInt(quantityInput.value, 10) || 1;
      val++;
      quantityInput.value = val;
      this.quantity = val;
      this.quantityChangedInModal = true;
      this.updateQuantityAndPrice();
    });

    quantityInput?.addEventListener('input', () => {
      let val = parseInt(quantityInput.value, 10);
      if (isNaN(val) || val < 1) {
        quantityInput.value = 1;
        this.quantity = 1;
      } else {
        this.quantity = val;
      }
      this.quantityChangedInModal = true;
      this.updateQuantityAndPrice();
    });
  }

  setSubscriptions(subscriptions) {
    this.subscriptions = subscriptions || [];
    this.updateSubscriptionInfo();
    this.updateDeliveryInfo();
    this.renderSubscriptionList();
  }

  setAddedProduct(product) {
    this.product = product;
  }

  setProductVariantId(variant) {
    this.productVariant = variant;
    this.setProductVariantPrice(variant);
    this.updateSelectedProductInfo();
  }

  setProductSubscriptionInterval(interval = 2) {
    this.productSubscriptionInterval = interval;
    this.updateSelectedProductInfo();
  }

  setProductId(id) {
    this.productId = id;
  }

  updateDeliveryInfo() {
    if (!this.subscriptions.length || !this.modal) return;

    const subscription = this.subscriptions[0];
    const deliveryContainer = this.modal.querySelector('.subscription-modal__delivery p');
    const iconEl = this.modal.querySelector('.subscription-modal__delivery .inpost-icon');
    if (!deliveryContainer) return;

    let deliveryText = '';

    if (subscription.source === 'JUO') {
      const rawShippingOption = subscription.raw.customAttributes.find((attr) => attr.key === 'Shipping');
      const shippingOption = rawShippingOption?.value || '';

      if (shippingOption.includes('Kurier')) {
        const addressObj = subscription.deliveryMethod?.address || '';
        if (addressObj) {
          const address = [addressObj.address1, addressObj.zip, addressObj.city].filter(Boolean).join(', ');
          deliveryText = address;
        } else {
          deliveryText = 'Adres nieznany';
        }
        iconEl?.classList.remove('locker-icon');
        iconEl?.classList.add('courier-icon');
      } else if (shippingOption.includes('InPost') || shippingOption.includes('Inpost')) {
        const inpostAddress = subscription.raw.customAttributes;
        const inpostName = inpostAddress.find((attr) => attr.key === 'InPost Name')?.value;
        deliveryText = inpostName ? `Paczkomat: ${inpostName}` : 'Paczkomat: nieznany';
        iconEl?.classList.remove('courier-icon');
        iconEl?.classList.add('locker-icon');
      } else {
        deliveryText = 'Metoda dostawy nieznana';
        iconEl?.classList.remove('courier-icon', 'locker-icon');
      }
    } else {
      deliveryText = 'Brak informacji o dostawie';
      iconEl?.classList.remove('courier-icon', 'locker-icon');
    }

    deliveryContainer.textContent = deliveryText;
  }

  updateSubscriptionInfo() {
    if (!this.subscriptions.length || !this.modal) return;
    const subscription = this.subscriptions[0];
    this.modal.querySelector('[data-sub-date]').textContent = this.formatDateWithDots(subscription.nextBillingDate);
    this.nextBillingDate = subscription.nextBillingDate;
  }

  renderSubscriptionList() {
    if (!this.modal || !this.subscriptions.length) return;
    const listContainer = this.modal.querySelector('.subscription-modal__product-list');
    if (!listContainer) return;

    listContainer.innerHTML = '';

    this.subscriptions.forEach((sub) => {
      if (sub.source === 'JUO') {
        const lines = Array.isArray(sub?.raw?.lines) ? sub.raw.lines : [];
        lines.forEach((line) => {
          const productName = line?.title || 'Brak nazwy';
          const itemEl = document.createElement('div');
          itemEl.className = 'subscription-item';
          itemEl.textContent = productName;
          listContainer.appendChild(itemEl);
        });
      }
    });
  }

  updateQuantityAndPrice() {
    const quantitySpan = this.modal?.querySelector('.product-quantity');
    if (quantitySpan) {
      quantitySpan.textContent = `${this.quantity}`;
    }

    const totalPriceSpan = this.modal?.querySelector('.total-price');
    if (totalPriceSpan) {
      totalPriceSpan.textContent = this.formatPrice(this.price * this.quantity);
    }
  }

  updateSelectedProductInfo() {
    if (!this.modal || !this.product || !this.productVariant) return;

    const productContainer = this.modal.querySelector('.subscription-modal__product');
    if (!productContainer) return;

    const variant = this.product.variants?.find((v) => String(v.id) === String(this.productVariant));
    if (!variant) {
      productContainer.innerHTML = '<p>Nie znaleziono wariantu produktu.</p>';
      return;
    }

    function getMonthWord(n) {
      if (n === 1) return 'miesiąc';
      if (n === 2 || n === 3 || n === 4) return 'miesiące';
      return 'miesięcy';
    }

    const productTitle = this.product.title;
    const imageSrc = variant.featured_image?.src || this.product.featured_image;
    const variantTitle = variant.title === 'Default Title' ? '' : variant.title;
    const interval = this.productSubscriptionInterval;
    const intervalText = `${interval} ${getMonthWord(interval)}`;
    const price = this.price ? this.formatPrice(this.price) : '—';
    const totalPrice = this.price ? this.formatPrice(this.price * this.quantity) : '—';

    // Tampony – breakdown
    let tamponsBreakdownHTML = '';
    if (this.product.id === 6855803666484 && variantTitle.includes('własna konfiguracja')) {
      const tamponsMini = document.querySelector('input[name="properties[mini]');
      const tamponsRegular = document.querySelector('input[name="properties[regular]');
      const tamponsSuper = document.querySelector('input[name="properties[super]');
      if (tamponsMini?.value > 0 || tamponsRegular?.value > 0 || tamponsSuper?.value > 0) {
        tamponsBreakdownHTML = `
          <div class="text-xs text-gray100 font-normal mb-2">
            <p class="mb-0.5">Mini: ${tamponsMini?.value || 0}</p>
            <p class="mb-0.5">Regular: ${tamponsRegular?.value || 0}</p>
            <p class="mb-0.5">Super: ${tamponsSuper?.value || 0}</p>
          </div>
        `;
      }
    }

    // Wielorazowa maszynka do golenia – banner
    if (this.product.id === 8583619215695) {
      this.showBladesBanner();
    }

    productContainer.innerHTML = `
      <div class="subscription-modal__product-info flex items-center gap-4 bg-bg-white rounded p-3">
        <img src="${imageSrc}" alt="${productTitle}" class="w-[60px] h-[60px] self-start rounded-lg" width="60" height="60" />
        <div class="flex-1">
          <p class="text-xs font-medium mb-2">${productTitle}</p>
          <p class="text-xs text-gray100 font-normal mb-2 ">${variantTitle.includes('własna konfiguracja') ? 'Własna konfiguracja' : variantTitle}</p>
          ${tamponsBreakdownHTML}
          <p class="text-xs text-gray100 font-normal">Dostawa co ${intervalText}</p>
          <div class="flex justify-between">
            <div>
              <span class="product-quantity text-xs font-normal">${this.quantity}</span>
              <span class="text-xs font-normal">x</span>
              <span class="price text-xs font-normal tracking-[0.26px]">${price}</span>
            </div>
            <div class="total-price tracking-[0.26px] font-semibold text-xs flex items-center">${totalPrice}</div>
          </div>
        </div>
      </div>
    `;
  }

  formatDateWithDots(dateString) {
    const [year, month, day] = dateString.split('-');
    return `${day}.${month}.${year}`;
  }

  async handleProductAddedToSubscriptionSuccess() {
    const modalContent = document.querySelector('.subscription-modal__content');
    const modalHeader = document.querySelector('.subscription-modal__header');

    modalContent.innerHTML = `
      <div class="subscription-modal__success-info">
        <svg class="w-[30px]" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.50708 7.71383L6.55923 9.76598L12.3721 3.95312" stroke="#166446" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M13.6349 6.6257C13.7463 7.07142 13.7927 7.52643 13.7927 8C13.7927 11.5843 10.8863 14.5 7.29272 14.5C3.70844 14.5 0.792725 11.5843 0.792725 8C0.792725 4.40643 3.70844 1.5 7.29272 1.5C8.33272 1.5 9.3263 1.75071 10.1992 2.18713" stroke="#166446" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p class="text-[#166446] font-semibold text-xs">Produkt został dodany do Twojej subskrypcji, zamówienie zostanie zrealizowane ${this.formatDateWithDots(this.nextBillingDate)}</p>
      </div> 
      <div class="subscription-modal__buttons-wrapper">
        <a href="/tools/recurring/login" class="subscription-modal__summary-link mb-5 bg-white text-black w-full !border-[1px] border-[#C7C7C7] h-[40px] uppercase flex justify-center px-[6px] py-[9.5px] text-[11px] tracking-[1.78px] font-semibold rounded-sm items-center">Zobacz podsumowanie zamówienia</a>
        <button class="subscription-modal__continue-btn bg-text-black01 text-white w-full !border-[1px] h-[40px] uppercase flex justify-center px-[6px] py-[9.5px] text-xs font-semibold rounded-sm items-center tracking-[1.78px]">Kontynuuj zakupy</button>
      </div>
    `;

    modalHeader.innerHTML = `
      <button class="subscription-modal__summary-close absolute top-4 text-xl">
        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17"><path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path></svg>
      </button>
      <h3 class="leading-[11px] font-semibold text-[11px] tracking-[0.16em] text-center uppercase">podsumowanie</h3>
    `;

    const resetView = () => {
      document.body.style.overflow = 'auto';
      this.render();
      this.updateSubscriptionInfo();
      this.updateDeliveryInfo();
      this.renderSubscriptionList();
      this.initQuantityControls();
      this.quantity = 1;
    };

    modalContent.querySelector('.subscription-modal__continue-btn')?.addEventListener('click', () => {
      dataLayerPromise({
        event: 'add_sub_modal',
        section: 'success_sub_modal',
        element: 'continue_button',
        element_value: this.product.title,
        interaction_type: 'click',
      });
      resetView();
    });

    modalHeader.querySelector('button.subscription-modal__summary-close')?.addEventListener('click', resetView);
    document.querySelector('.subscription-modal__overlay')?.addEventListener('click', resetView);

    const summaryLink = modalContent.querySelector('.subscription-modal__summary-link');
    if (summaryLink) {
      summaryLink.addEventListener('click', async (e) => {
        document.body.style.overflow = 'auto';
        e.preventDefault();
        await dataLayerPromise({
          event: 'add_sub_modal',
          section: 'success_sub_modal',
          element: 'summary_button',
          element_value: this.product.title,
          interaction_type: 'click',
        });
        continueLinkClickEvent(e, summaryLink.href);
      });
    }
  }

  async refetchSubscriptions() {
    try {
      const fetcher = new SubscriptionFetcher();
      await fetcher.init();
      const subscriptions = await fetcher.getClosestSubscriptions();
      if (subscriptions.length) {
        this.subscriptions = subscriptions || [];
      }
    } catch (err) {
      console.error('Błąd podczas ponownego pobierania subskrypcji:', err);
    }
  }

  removeCloseListeners() {
    const closeBtn = this.modal.querySelector('.subscription-modal__close');
    const overlay = this.modal.querySelector('.subscription-modal__overlay');
    if (closeBtn) closeBtn.removeEventListener('click', this.handleCloseClick);
    if (overlay) overlay.removeEventListener('click', this.handleCloseClick);
  }

  handleDisplayErrorView() {
    const modalContent = document.querySelector('.subscription-modal__content');
    const modalHeader = document.querySelector('.subscription-modal__header');

    modalHeader.innerHTML = `
      <button class="subscription-modal__summary-close absolute top-4 text-xl">
        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17"><path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path></svg>
      </button>
      <h3 class="leading-[11px] font-semibold text-[11px] tracking-[0.16em] text-center uppercase">Wystąpił błąd</h3>
    `;

    modalContent.innerHTML = `
      <div class="subscription-modal__error-info">
        <svg class="w-[30px]" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 13.6729C11.0899 13.6729 14 10.7627 14 7.17285C14 3.583 11.0899 0.672852 7.5 0.672852C3.91015 0.672852 1 3.583 1 7.17285C1 10.7627 3.91015 13.6729 7.5 13.6729Z" stroke="#A54648" stroke-width="0.722222" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M5.20166 4.87354L9.79785 9.46973" stroke="#A54648" stroke-width="0.722222" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9.79785 4.87402L5.20166 9.47021" stroke="#A54648" stroke-width="0.722222" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p class="text-[#A54648] font-semibold text-xs">Nie udało się dodać tego produktu do zamówienia. Spróbuj ponownie później lub skontaktuj się z nami.</p>
      </div> 
      <div class="subscription-modal__buttons-wrapper">
        <button class="subscription-modal__continue-btn bg-text-black01 text-white w-full !border-[1px] h-[40px] uppercase flex justify-center px-[6px] py-[9.5px] text-xs font-semibold rounded-sm items-center tracking-[1.78px]">Kontynuuj zakupy</button>
      </div>
    `;

    const resetView = () => {
      document.body.style.overflow = 'auto';
      this.render();
      this.updateSubscriptionInfo();
      this.updateDeliveryInfo();
      this.renderSubscriptionList();
      this.initQuantityControls();
      this.quantity = 1;
    };

    modalContent.querySelector('.subscription-modal__continue-btn')?.addEventListener('click', async () => {
      await dataLayerPromise({
        event: 'add_sub_modal',
        section: 'error_sub_modal',
        element: 'continue_button',
        element_value: this.product.title,
        interaction_type: 'click',
      });
      resetView();
    });

    document
      .querySelector('.subscription-modal__header .subscription-modal__summary-close')
      ?.addEventListener('click', resetView);
  }

  handleAddProductClick() {
    const subscription = this.subscriptions[0];
    const variantId = this.productVariant;
    const interval = this.productSubscriptionInterval;
    const productId = this.productId;
    const quantity = this.quantity;

    this.removeCloseListeners();

    dataLayerPromise({
      event: 'add_sub_modal',
      section: 'sub_modal',
      element: 'add_sub_button',
      element_value: this.product.title,
      interaction_type: 'click',
    });

    if (!subscription || !variantId || !interval) {
      console.error('❌ Brakuje danych do dodania produktu');
      return;
    }

    const button = this.modal.querySelector('.subscription-modal__confirm');
    button.disabled = true;

    const loader = this.modal.querySelector('.subscription-modal__confirm .loader');
    loader.style.display = 'inline-block';

    const buttonText = this.modal.querySelector('.subscription-modal__confirm span');
    buttonText.innerHTML = '';

    try {
      addProductToSubscription({
        subscription,
        productId,
        variantId,
        quantity,
        interval,
        refetch: () => this.refetchSubscriptions(),
        onSuccess: () => this.handleProductAddedToSubscriptionSuccess(),
        onError: () => this.handleDisplayErrorView(),
      });
    } catch (e) {
      console.error('Nie udało się dodać produktu do subskrypcji', e);
    }
  }

  redirectToLogin() {
    const currentPath = window.location.pathname + window.location.search;
    const returnTo = encodeURIComponent(currentPath);
    window.location.href = `/account/login?return_to=${returnTo}`;
  }

  open() {
    if (!this.subscriptions.length && !window.customerData) {
      dataLayerPromise({
        event: 'add_sub_modal',
        section: 'recharge_widget',
        element: 'text_button',
        element_value: this.product.title,
        interaction_type: 'click',
        is_logged: false,
      }).then(() => this.redirectToLogin());
      return;
    }
    dataLayerPromise({
      event: 'add_sub_modal',
      section: 'recharge_widget',
      element: 'text_button',
      element_value: this.product.title,
      interaction_type: 'click',
      is_logged: true,
    });

    if (!this.subscriptions.length && window.customerData) {
      console.warn('[SubscriptionModal] Brak aktywnych subskrypcji, pokazuję komunikat');
      this.showNoSubscriptionsView();
    }

    dataLayerPromise({
      event: 'add_sub_modal',
      section: 'sub_modal',
      element_value: this.product.title,
      interaction_type: 'open',
    });

    document.body.style.overflow = 'hidden';
    this.modal.classList.remove('hidden');
    requestAnimationFrame(() => {
      this.modal.querySelector('.subscription-modal__panel').classList.remove('translate-x-full');
    });
  }

  close() {
    const panel = this.modal.querySelector('.subscription-modal__panel');
    panel.classList.add('translate-x-full');
    document.body.style.overflow = 'auto';
    setTimeout(() => {
      this.modal.classList.add('hidden');
    }, 300);
  }

  showNoSubscriptionsView() {
    if (!this.modal) return;
    const modalContent = document.querySelector('.subscription-modal__content');
    const modalHeader = document.querySelector('.subscription-modal__header');

    modalHeader.innerHTML = `
      <button class="subscription-modal__summary-close absolute top-4 text-xl">
        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17"><path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path></svg>
      </button>
      <h3 class="leading-[11px] font-semibold text-[11px] tracking-[0.16em] text-center uppercase">Brak aktywnych subskrypcji</h3>
    `;

    modalContent.innerHTML = `
      <div class="subscription-modal__error-info">
        <svg class="w-[30px]" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 13.6729C11.0899 13.6729 14 10.7627 14 7.17285C14 3.583 11.0899 0.672852 7.5 0.672852C3.91015 0.672852 1 3.583 1 7.17285C1 10.7627 3.91015 13.6729 7.5 13.6729Z" stroke="#A54648" stroke-width="0.722222" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M5.20166 4.87354L9.79785 9.46973" stroke="#A54648" stroke-width="0.722222" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9.79785 4.87402L5.20166 9.47021" stroke="#A54648" stroke-width="0.722222" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p class="text-[#A54648] font-semibold text-xs">Nie znaleźliśmy na Twoim koncie aktywnych subskrypcji.</p>
      </div> 
      <div class="subscription-modal__buttons-wrapper">
        <button class="subscription-modal__continue-btn bg-text-black01 text-white w-full !border-[1px] h-[40px] uppercase flex justify-center px-[6px] py-[9.5px] text-xs font-semibold rounded-sm items-center tracking-[1.78px]">Zamknij</button>
      </div>
    `;

    const resetView = () => {
      document.body.style.overflow = 'auto';
      this.render();
      this.initQuantityControls();
      this.quantity = 1;
    };

    modalContent.querySelector('.subscription-modal__continue-btn')?.addEventListener('click', resetView);
    modalHeader.querySelector('button.subscription-modal__summary-close')?.addEventListener('click', resetView);
    document.querySelector('.subscription-modal__overlay')?.addEventListener('click', resetView);
  }

  formatPrice(price) {
    const number = Number(price);
    if (isNaN(number)) return '';
    return number.toFixed(2).replace('.', ',') + ' zł';
  }

  setQuantity(value) {
    if (this.quantityChangedInModal) return;

    const quantity = Number(value);
    this.quantity = isNaN(quantity) || quantity < 1 ? 1 : quantity;

    const quantityInputInModal = this.modal.querySelector('.quantity_input');
    if (quantityInputInModal) {
      quantityInputInModal.value = this.quantity;
    }

    const quantitySpan = this.modal?.querySelector('.product-quantity');
    if (quantitySpan) {
      quantitySpan.textContent = `${this.quantity}`;
    }

    const totalPriceSpan = this.modal?.querySelector('.total-price');
    if (totalPriceSpan) {
      const total = this.quantity * this.price;
      totalPriceSpan.textContent = this.formatPrice(total);
    }
  }

  render() {
    const minus_svg = `<svg width="13" height="2" viewBox="0 0 13 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.7928 0.84375H6.79276H0.792725" stroke="black"/></svg>`;
    const plus_svg = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" width="16" height="16" viewBox="0 0 16 16"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.5 1C8.5 0.723858 8.27614 0.5 8 0.5C7.72386 0.5 7.5 0.723858 7.5 1V7.5H1C0.723858 7.5 0.5 7.72386 0.5 8C0.5 8.27614 0.723858 8.5 1 8.5H7.5L7.5 15C7.5 15.2761 7.72386 15.5 8 15.5C8.27614 15.5 8.5 15.2761 8.5 15V8.5H15C15.2761 8.5 15.5 8.27614 15.5 8C15.5 7.72386 15.2761 7.5 15 7.5H8.5L8.5 1Z" /></svg>`;

    this.innerHTML = `
      <div id="add-sub-modal" class="subscription-modal fixed inset-0 z-50 hidden">
        <div class="subscription-modal__overlay absolute inset-0 bg-black bg-opacity-50 transition-opacity"></div>
        <div class="subscription-modal__panel fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out translate-x-full">
          <div class="subscription-modal__header flex justify-center ">
            <button class="subscription-modal__close absolute top-4 text-xl">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17"><path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path></svg>
            </button>
            <h3 class="leading-[11px] font-semibold text-[11px] tracking-[0.16em] text-center uppercase">Dodaj produkt do subskrypcji</h3>
          </div>
          <div class="subscription-modal__content">
            <div class="subscription-modal__info">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 18 18" fill="none">
                <path d="M8.99992 17C13.4182 17 16.9999 13.4183 16.9999 9C16.9999 4.58172 13.4182 1 8.99992 1C4.58164 1 0.999916 4.58172 0.999916 9C0.999916 13.4183 4.58164 17 8.99992 17Z" stroke="#1C4488" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M8.99984 8.17969V12.4874" stroke="#1C4488" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M8.99992 6.20762C9.24845 6.20762 9.44992 6.00615 9.44992 5.75762C9.44992 5.50909 9.24845 5.30762 8.99992 5.30762C8.75139 5.30762 8.54992 5.50909 8.54992 5.75762C8.54992 6.00615 8.75139 6.20762 8.99992 6.20762Z" stroke="#1C4488" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
              <p>Dodaj produkt do subskrypcji, aby otrzymać go wraz ze swoim najbliższym zamówieniem.</p>
            </div>

            <div class="subscription-modal__label">
              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.7256 7.48538L8.92296 2.88886C8.8544 2.82871 8.75155 2.82871 8.67441 2.88886L2.87181 7.48538C2.82039 7.51975 2.79468 7.5799 2.79468 7.64004V14.6422C2.79468 14.7539 2.88038 14.8398 2.99181 14.8398H14.5884C14.6998 14.8398 14.7856 14.7539 14.7856 14.6422V7.64004C14.8027 7.5799 14.777 7.51975 14.7256 7.48538Z" stroke="#1D1D1B" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10.9456 14.8427V10.5469H6.66003V14.8427" stroke="#1D1D1B" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <p>wysyłka do</p>
            </div>

            <div class="subscription-modal__delivery">
              <div>
                <div class="inpost-icon"></div>
                <p></p>
              </div>
            </div>

            <div class="subscription-modal__order">
              <p><span class="font-medium text-[13px] tracking-[0.26px]" data-sub-date></span></p>
              <div class="subscription-modal__product-list"></div>
            </div>

            <div class="subscription-modal__label">
              <p>Dodaj produkt</p>
            </div>

            <div class="subscription-modal__product mb-5">
              <span data-variant-info></span>
            </div>

            <div class="subscription-modal__blades-banner mt-5 mb-5 bg-[#F2E8E8] hidden p-3 text-xs leading-5 text-blue-800 rounded">
              W pierwszym zamówieniu subskrypcyjnym otrzymasz <b>wielorazową rączkę + 2 główki</b>, a w kolejnych cztery <b>główki</b> już bez rączki.
            </div>

            <div class="subscription-modal__buttons mt-auto">
              <div class="quantity flex items-center justify-center w-[90px] h-[40px] rounded-sm border border-gray40 bg-bg-white">
                <button class="quantity_button no-js-hidden inline-flex items-center justify-center w-[30px] h-[40px] pl-5 z-[2]" name="minus" type="button">${minus_svg}</button>
                <input class="quantity_input w-[30px] bg-bg-white inline-flex outline-0 border-0 text-center font-sans h-full text-xs font-semibold leading-none tracking-[2px] uppercase flex-grow" type="number" name="quantity" min="1" value="1">
                <button class="quantity_button no-js-hidden inline-flex items-center justify-center w-[30px] h-[40px] pr-5 z-[2]" name="plus" type="button">${plus_svg}</button>
              </div>

              <button class="subscription-modal__confirm btn btn--primary-2 !normal-case px-4 py-2 bg-black text-white rounded-sm">
                <span class="text-[13px] tracking-[0.52px]">Dodaj do subskrypcji</span>
                <div class="loader hidden"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.modal = this.querySelector('#add-sub-modal');
    const closeBtn = this.modal.querySelector('.subscription-modal__close');
    const overlay = this.modal.querySelector('.subscription-modal__overlay');

    if (closeBtn) closeBtn.addEventListener('click', this.handleCloseClick);
    if (overlay) overlay.addEventListener('click', this.handleCloseClick);
  }

  showBladesBanner() {
    if (!this.modal || !this.product) return;
    const bannerDrawer = document.querySelector('.subscription-modal__blades-banner');
    if (bannerDrawer) bannerDrawer.classList.remove('hidden');
  }
}

customElements.define('subscription-modal', SubscriptionModal);

class SubscriptionFetcher {
  constructor() {
    this.initialized = false;
  }

  async init() {
    this.initialized = true;
    return true;
  }

  async fetchJUOSubscriptions() {
    const res = await fetch('/apps/juo/api/customer/subscriptions');
    if (!res.ok) throw new Error('JUO API error');
    return res.json();
  }

  async getClosestSubscriptions() {
    const juoSubsRaw = await this.fetchJUOSubscriptions().catch(() => []);

    const juoSubs = juoSubsRaw
      .filter((s) => s.status === 'ACTIVE' && s.nextBillingDate)
      .map((s) => ({
        source: 'JUO',
        id: s.id,
        nextBillingDate: s.nextBillingDate ? s.nextBillingDate.slice(0, 10) : s.nextBillingDate,
        deliveryMethod: {
          address: s.deliveryMethod.address,
          shippingOption: s.deliveryMethod?.shippingOption?.title,
        },
        raw: s,
      }));

    if (!juoSubs.length) return [];

    const closestDate = juoSubs
      .map((s) => new Date(s.nextBillingDate))
      .sort((a, b) => a - b)[0]
      .toISOString()
      .split('T')[0];

    const closestSubs = juoSubs.filter((s) => s.nextBillingDate.startsWith(closestDate));
    return closestSubs;
  }
}

async function addProductToSubscription({
  subscription,
  productId,
  variantId,
  quantity,
  interval,
  refetch,
  onSuccess,
  onError,
}) {
  if (!subscription || !subscription.source) {
    console.error('❌ Nieprawidłowa subskrypcja');
    return;
  }

  // Tampony – atrybuty JUO
  let juo_tamponsProperties;
  if (variantId === '40493254672436') {
    const tamponsMini = document.querySelector('input[name="properties[mini]');
    const tamponsRegular = document.querySelector('input[name="properties[regular]');
    const tamponsSuper = document.querySelector('input[name="properties[super]');

    juo_tamponsProperties = [
      { key: 'mini', value: tamponsMini?.value },
      { key: 'regular', value: tamponsRegular?.value },
      { key: 'super', value: tamponsSuper?.value },
    ];
  }

  if (subscription.source === 'JUO') {
    let deliveryMethod = subscription.deliveryMethod.shippingOption;

    if (deliveryMethod === 'Inpost Paczkomat 24/7') {
      deliveryMethod = 'InPost Paczkomat 24/7';
    } else if (deliveryMethod === 'Inpost Paczkomaty 24/7') {
      deliveryMethod = 'InPost Paczkomat 24/7';
    } else if (deliveryMethod === 'InPost Paczkomaty 24/7') {
      deliveryMethod = 'InPost Paczkomat 24/7';
    } else if (typeof deliveryMethod === 'string' && deliveryMethod.includes('Kurier')) {
      deliveryMethod = 'InPost Kurier';
    }

    const subscriptionPayload = {
      productVariantId: variantId,
      quantity,
      shippingMethodHandle: deliveryMethod,
      billingPolicy: {
        intervalCount: interval,
        interval: 'MONTH',
      },
    };

    if (juo_tamponsProperties) {
      subscriptionPayload.customAttributes = juo_tamponsProperties;
    }

    try {
      const response = await fetch(`/apps/juo/api/customer/subscriptions/${subscription.id}/lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscriptionPayload),
      });

      if (!response.ok) {
        onError();
        return;
      }

      await refetch();
      onSuccess();
    } catch (err) {
      onError();
      console.error('❌ Błąd przy dodawaniu do JUO:', err);
    }
  } else {
    console.warn('⚠️ Nieobsługiwane źródło (spodziewano JUO):', subscription.source);
  }
}
